/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { SelectionModel } from '@angular/cdk/collections';
import { Injectable } from '@angular/core';
import { ApiEntity } from 'app/api/models/api-entity.model';
import { ListRequestModel, ListStorageModel } from 'app/api/models/list-request.model';
import { ApiDependenciesService } from 'app/api/services/api-dependencies.service';
import { IAddListService } from 'app/shared/interfaces/IAddListService.interface';
import { ActionableItemState } from 'app/shared/models/actionable-item-state';
import { CollectionResponse } from 'app/shared/models/collectionResponse.model';
import { Filter } from 'app/shared/models/filter.model';
import { EMPTY_LIST_RESULT_STATE, ListResultState } from 'app/shared/models/list-result-state.model';
import { SortOrder } from 'app/shared/models/sort-order.model';
import { DataService } from 'app/shared/services/data.service';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { BehaviorSubject, Subject } from 'rxjs';
import { downloadFileFromExtendedBlob } from '../helpers/file-download-helper';
import { ApiResponseSummary } from '../models/api-info.model';
import { Group } from '../models/group.model';
import { ConfirmationDialogService } from './confirmation-dialog.service';
// Base class for all services injected into components using the AddList component
// Provides the message hub for the mediator pattern
// Provides methods to be used to publish messages to the hub for distribution to subscribers
// The base service will be responsible for maintaining the state of the children in the list and interfacing with the API

@Injectable()
export abstract class ListServiceBase extends DataService<any> implements IAddListService {
	//#region Properties
	// the items to be rendered in the list
	itemList: any[] = [];
	groupedData: any[] = [];
	reducedGroups = [];
	groupBy = '';
	groupTitleColumn = '';
	defaultGroupSorting = true;
	selectedItemIds: number[] = [];
	expandedRowIds: number[] = [];
	expandRowsOnReload: boolean = false;
	selection = new SelectionModel<any>(true, []); // avoid using this property - use custom built-in functionality that uses ActionableItemState.itemSelected
	itemsCount: number;
	private unfilteredItemsCount: number | null = null;
	uiStateKey = null;
	useBlockUi = true;

	protected listRequest: ListRequestModel;
	protected pageCount: number;
	//#endregion

	get ListRequest() {
		return this.listRequest as ListRequestModel;
	}

	//#region Observables
	private itemDeleted = new Subject<number>();
	// Observable itemDeleted stream
	itemDeleted$ = this.itemDeleted.asObservable();

	private listReloaded = new BehaviorSubject<number>(null);
	// Observable listReloaded stream
	listReloaded$ = this.listReloaded.asObservable();

	protected itemAdded = new BehaviorSubject<number>(null);
	// Observable listReloaded stream
	itemAdded$ = this.itemAdded.asObservable();

	private blockingState = new BehaviorSubject<boolean>(false);
	blockingState$ = this.blockingState.asObservable();

	private listResultState = new BehaviorSubject<ListResultState<any>>(EMPTY_LIST_RESULT_STATE);
	/** Current displayed results, filtered result count, and known unfiltered total. */
	listResultState$ = this.listResultState.asObservable();
	//#endregion

	constructor(
		protected dependencies: ApiDependenciesService,
		protected confirmationDialogService: ConfirmationDialogService,
	) {
		super(dependencies);
		this.initialiseListRequest();
	}

	//#region Block UI
	stopBlockUI() {
		super.stopBlockUI();
		this.blockingState.next(false);
	}

	startBlockUI() {
		this.blockingState.next(true);
		super.startBlockUI();
	}
	//#endregion

	clearItems() {
		this.itemList = [];
		this.itemsCount = 0;
		this.publishListResultState();
	}

	/**
	 * Sets the total population when a consumer already knows it from an existing frontend source.
	 * This is additive and does not make a new API request.
	 */
	setUnfilteredItemsCount(totalCount: number | null): void {
		this.unfilteredItemsCount = totalCount;
		this.publishListResultState();
	}

	/** Replaces client-managed results and emits the same state used by API-backed reloads. */
	replaceItems(items: any[], resultCount: number = items.length, totalCount: number | null = this.unfilteredItemsCount): void {
		this.itemList = items ?? [];
		this.itemsCount = resultCount;
		if (totalCount !== null) this.unfilteredItemsCount = totalCount;
		this.publishListResultState();
		this.listReloaded.next(this.itemsCount);
	}

	//#region Endpoints

	// not all endpoints will be required for all implementations

	// OVERRIDE in derived class as required
	protected getListEndpoint(): string {
		return '/unknown-endpoint';
	}

	// OVERRIDE in derived class as required
	protected getCreateEndpoint(): string {
		return '/unknown-endpoint';
	}

	// OVERRIDE in derived class as required
	protected getUpdateEndpoint(itemId: number): string {
		return '/unknown-endpoint';
	}

	// OVERRIDE in derived class as required
	protected getCommandStubEndpoint(itemId: number): string {
		return '/unknown-endpoint';
	}

	// OVERRIDE in derived class as required
	protected getDeleteEndpoint(itemId: number): string {
		return '/unknown-endpoint';
	}

	// OVERRIDE in derived class as required
	protected getGetEditableItemEndpoint(itemId: number): string {
		return '/unknown-endpoint';
	}

	protected get exportListEndpoint(): string {
		return '/unknown-endpoint';
	}

	// #endregion

	//#region List Building

	public initialiseListRequest() {
		this.listRequest = new ListRequestModel();
	}

	// OVERRIDE in derived class
	createListViewModel(dataModel: ApiEntity) {
		return new ActionableItemState();
	}

	protected preloadStoreState() {
		this.selectedItemIds = this.getSelectedItemIds();
	}

	protected postloadRestoreState() {
		this.selectItemsFromIds(this.selectedItemIds);
	}

	// reloads the list using overridden methods for the implementation specific details
	// gets a list of items mapped to ApiEntity data models and then creates view models passing the data model as parameter
	async reload() {
		this.startBlockUI();
		this.preloadStoreState();

		await this.getListItems()
			.then((response: ApiEntity[]) => {
				this.itemList = [];
				response.forEach((dataModel: ApiEntity) => {
					const model: ActionableItemState = this.createListViewModel(dataModel);
					model.itemId = dataModel.id;
					this.itemList.push(model);
				});

				this.postloadRestoreState();

				this.applyGrouping();

				this.expandRows();

				this.publishListResultState();

				this.listReloaded.next(this.itemsCount);

				this.stopBlockUI();
			})
			.catch(() => {
				this.stopBlockUI();
			});
	}

	expandRows() {
		if (this.expandRowsOnReload) {
			this.itemList.forEach((item: any) => {
				var match = this.expandedRowIds.find((id) => id == item.itemId);
				if (match != undefined) {
					item.expanded = true;
				}
			});
		}
	}

	getListRequest() {
		return this.listRequest;
	}

	async getListItems(): Promise<ApiEntity[]> {
		let model = new Array<ApiEntity>();
		const url = this.getListEndpoint();

		const listRequest = this.getListRequest();
		if (url) {
			await this.setEndpoint(url)
				.queryCollection(listRequest)
				.then((response: CollectionResponse<any>) => {
					// console.debug(`loaded list for ${this.constructor.name}`);
					// console.debug(response);
					// console.table(response.items);

					model = this.mapList(response);
				});
		}

		return Promise.resolve(model);
	}

	mapList(responseList: CollectionResponse<any>): ApiEntity[] {
		const result: ApiEntity[] = [];
		if (responseList != null) {
			this.itemsCount = responseList.itemsCount;
			if (!this.listRequest.filters?.length) this.unfilteredItemsCount = responseList.itemsCount;
			this.pageCount = Math.ceil(this.itemsCount / this.listRequest.pageSize);
			responseList.items.forEach((responseItem) => {
				const mappedModel = this.getMappedListItemDataModel(responseItem);
				result.push(mappedModel);
			});
		}

		return result;
	}

	protected publishListResultState(): void {
		this.listResultState.next({
			results: [...(this.itemList ?? [])],
			resultCount: this.itemsCount ?? 0,
			totalCount: this.unfilteredItemsCount ?? (this.listRequest.filters?.length ? null : (this.itemsCount ?? 0)),
		});
	}

	// OVERRIDE in derived class
	getMappedListItemDataModel(responseItem: any): ApiEntity {
		return null;
	}

	//#endregion

	//#region Paging, Sorting and Filtering

	get currentPageItemCount(): number {
		return this.itemList ? this.itemList.length : 0;
	}

	get paginatorIndex(): number {
		return this.listRequest.pageNumber - 1;
	}

	pageSummary() {
		const start = Math.min(this.listRequest.pageSize * (this.listRequest.pageNumber - 1) + 1, this.itemsCount);
		const end = Math.min(start + this.listRequest.pageSize - 1, this.itemsCount);
		return 'Showing ' + start + ' to ' + end + ' of ' + this.itemsCount + ' items';
	}

	getSortIcon(sortColumn): string {
		const existingSort: SortOrder = this.listRequest.sort.length === 0 ? null : this.listRequest.sort[0];
		if (existingSort) {
			if (existingSort.field === sortColumn) {
				return existingSort.direction === 'asc' ? 'chevron-up' : 'chevron-down';
			}
		}
		return '';
	}

	setSort(sortColumn, sortDirection) {
		this.listRequest.sort = [new SortOrder(sortColumn, sortDirection)];
		this.listRequest.pageNumber = 1;
		this.saveState();
		this.reload();
	}

	setPaging(pageSize, pageNumber) {
		this.listRequest.pageSize = pageSize;
		this.listRequest.pageNumber = pageNumber;
		this.saveState();
		this.reload();
	}

	// state is managed on the filter component rather than in the list service
	setFilters(filters: Array<Filter>, resetPageNumber = true) {
		this.listRequest.filters = filters;
		if (resetPageNumber) {
			this.listRequest.pageNumber = 1;
		}
		this.reload();
	}

	//#endregion

	//#region State
	loadState() {
		if (this.uiStateKey) {
			const request = this.dependencies.uiStateService.getUiStateForKey(this.uiStateKey);
			if (request) {
				this.listRequest.paged = request.paged;
				this.listRequest.pageNumber = request.pageNumber;
				this.listRequest.pageSize = request.pageSize;
				this.listRequest.sort = request.sort;
				this.groupBy = request.groupBy;
			} else {
				this.initialiseListRequest();
				this.groupBy = '';
			}
		}
	}

	saveState() {
		if (this.uiStateKey) {
			const request = new ListStorageModel(this.listRequest.paged, this.listRequest.pageNumber, this.listRequest.pageSize, this.listRequest.sort, this.groupBy);
			this.dependencies.uiStateService.setUiState(this.uiStateKey, request);
		}
	}
	//#endregion

	//#region Multi Select

	// update the itemSelected Property of all visible items in the list
	selectAll(selectValue: boolean) {
		for (let i = 0, len = this.itemList.length; i < len; i++) {
			if (!selectValue || this.isItemSelectable(this.itemList[i].dataModel)) {
				this.itemList[i].itemSelected = selectValue;
			}
		}
	}

	toggleSelectAll() {
		this.selectAll(!this.allSelected);
	}

	get selectableListLength(): number {
		return this.itemList.filter((item) => this.isItemSelectable(item.dataModel)).length;
	}

	get allSelected(): boolean {
		const count = this.itemList.filter((item) => item.itemSelected === true).length;
		return count === this.selectableListLength;
	}

	get noneSelected(): boolean {
		const count = this.itemList.filter((item) => item.itemSelected === true).length;
		return count === 0;
	}

	get anySelected(): boolean {
		const count = this.itemList.filter((item) => item.itemSelected === true).length;
		return count > 0;
	}

	// populate number array with the id of visible selected items in the list
	getSelectedItemIds(): number[] {
		if (this.itemList == null) {
			return [];
		}

		const selectedIds: number[] = [];
		for (let i = 0, len = this.itemList.length; i < len; i++) {
			const model = this.itemList[i];
			if (model.itemSelected === true) {
				selectedIds.push(model.itemId);
			}
		}
		return selectedIds;
	}

	private selectItemsFromIds(selectedIds: number[]) {
		selectedIds.forEach((id) => {
			const model = this.findViewModelFromItemId(id);
			if (model != null && this.isItemSelectable(model.dataModel)) {
				model.itemSelected = true;
			}
		});
	}

	// override to exclude an item form being reselected
	protected isItemSelectable(datamodel: any): boolean {
		return true;
	}
	//#endregion

	//#region Item Processing

	async addNew(item: any, endpoint: string = null): Promise<number> {
		let newRecordId: number;
		this.startBlockUI();
		await this.setEndpoint(this.getCreateEndpoint())
			.save(item)
			.then((response) => {
				newRecordId = response.newRecordId;
				this.itemAdded.next(response.newRecordId);

				this.reload().then(() => {
					this.stopBlockUI();
				});
			})
			.catch(() => {
				this.stopBlockUI();
			});

		return Promise.resolve(newRecordId);
	}

	findViewModelFromItemId(itemId: number): ActionableItemState {
		const viewModel = this.itemList.find((x) => x.itemId === itemId);
		if (viewModel === null) {
			return null;
		}

		return viewModel;
	}

	async sendCommand(itemId: number, command: string, data: any = null) {
		const viewModel = this.findViewModelFromItemId(itemId);
		if (viewModel != null) {
			this.startBlockUI();
			await this.setEndpoint(this.getCommandStubEndpoint(itemId) + '/' + command)
				.command(data)
				.then((response) => {})
				.catch((error) => {})
				.finally(() => {
					this.stopBlockUI();
				});
		}
	}

	//viewModelCheck used to skip check when item may not be in the list or list not available
	async sendCommandFollowingConfirmation(itemId: number, command: string, data: any = null, title: string = 'Confirm Action', message: string = 'Are you sure you want to process this item?', viewModelCheck: boolean = true) {
		const viewModel = this.findViewModelFromItemId(itemId);

		if (!viewModelCheck || viewModel != null) {
			this.confirmationDialogService.openConfirmationDialog(title, message, () => {
				this.startBlockUI();
				this.setEndpoint(this.getCommandStubEndpoint(itemId) + '/' + command)
					.command(data)
					.then(() => {
						this.stopBlockUI();
						this.reload();
					})
					.catch((error) => {
						this.stopBlockUI();
					});
			});

			// const dialogRef = this.dialog.open(ConfirmationModalComponent, {
			//   data: { title: title, message: message },
			// });

			// dialogRef.componentInstance.onClose.subscribe((result) => {
			//   if (result.result) {
			//     this.startBlockUI();
			//     this.setEndpoint(this.getCommandStubEndpoint(itemId) + '/' + command)
			//       .command(data)
			//       .then(() => {
			//         this.stopBlockUI();
			//         this.reload();
			//       })
			//       .catch((error) => {
			//         this.stopBlockUI();
			//       });
			//   }
			// });
		}
	}

	// update the item and reload the list if the update was successful
	// block the UI whilst the update is being performed
	async updateItem(itemId: number, dataToUpdate: any, endpoint: string = null, reloadList: boolean = true): Promise<ApiResponseSummary<any>> {
		let result: ApiResponseSummary<any>;
		this.startBlockUI();
		const endpointToUse = endpoint == null ? this.getUpdateEndpoint(itemId) : endpoint;
		await this.setEndpoint(endpointToUse)
			.update(dataToUpdate)
			.then((response) => {
				result = response;
				if (result.success === true) {
					if (reloadList) {
						this.reload();
					}
					this.stopBlockUI();
				}
			})
			.catch(() => {
				this.stopBlockUI();
			});
		this.stopBlockUI();
		return Promise.resolve(result);
	}

	async saveItem(itemId: number, dataToUpdate: any, endpoint: string) {
		// find in list and process
		const viewModel = this.findViewModelFromItemId(itemId);

		if (viewModel != null) {
			await this.setEndpoint(endpoint)
				.save(dataToUpdate)
				.then(() => {
					this.reload();
				});
		}
	}

	async deleteItem(itemId: number, title: string = 'Confirm Delete', message: string = 'Are you sure you want to delete this item?') {
		const viewModel = this.findViewModelFromItemId(itemId);
		if (viewModel != null) {
			this.confirmationDialogService.openConfirmationDialog(title, message, () => {
				this.blockUI.start();
				this.setEndpoint(this.getDeleteEndpoint(viewModel.itemId))
					.delete()
					.then(() => {
						this.reload()
							.then(() => {
								this.itemDeleted.next(viewModel.itemId);
								this.blockUI.stop();
							})
							.catch((error) => {
								this.blockUI.stop();
							});
					});
			});

			// const dialogRef = this.dialog.open(ConfirmationModalComponent, {
			//   data: { title: title, message: message },
			// });
			// dialogRef.componentInstance.onClose.subscribe((result) => {
			//   if (result.result) {
			//     this.blockUI.start();
			//     this.setEndpoint(this.getDeleteEndpoint(viewModel.itemId))
			//       .delete()
			//       .then(() => {
			//         this.reload()
			//           .then(() => {
			//             this.itemDeleted.next(viewModel.itemId);
			//             this.blockUI.stop();
			//           })
			//           .catch((error) => {
			//             this.blockUI.stop();
			//           });
			//       });
			//   }
			// });
		}
	}

	// OVERRIDE in derived class
	protected getMappedEditableItemDataModel(response: any): ApiEntity {
		return null;
	}

	async getEditableItemDetail(itemId: number, endpoint: string = null): Promise<ApiEntity> {
		this.startBlockUI();

		const endpointToUse = endpoint == null ? this.getGetEditableItemEndpoint(itemId) : endpoint;

		let model: ApiEntity;
		await this.setEndpoint(endpointToUse)
			.getSingle()
			.then((response) => {
				model = this.getMappedEditableItemDataModel(response);
				this.stopBlockUI();
			})
			.catch(() => {
				this.stopBlockUI();
			});

		return Promise.resolve(model);
	}

	async exportToExcel() {
		await this.setEndpoint(this.exportListEndpoint)
			.getFileAsBlobWithPost(this.listRequest)
			.then((response) => {
				downloadFileFromExtendedBlob(response);
			});
	}

	//#endregion

	//#region Grouping

	private applyGrouping() {
		const groupFields = this.getGroupFields();
		if (groupFields.length > 0) {
			if (this.defaultGroupSorting) {
				// sort the item list by the first group by value and then by the sorted column
				const sortByColumn = groupFields[0];
				const listRequestSort = this.listRequest.sort.length === 0 ? null : this.listRequest.sort[0];

				this.itemList = this.itemList.sort(
					(a, b) => this.sortFn(a.dataModel[sortByColumn], b.dataModel[sortByColumn]) || (listRequestSort ? this.sortFn(a.dataModel[listRequestSort.field], b.dataModel[listRequestSort.field], listRequestSort.direction === 'asc') : 0),
				);
			}
			this.groupedData = this.addGroups(this.itemList, groupFields);
		} else {
			this.groupedData = this.itemList;
		}
	}

	addGroups(data: any[], groupByColumns: string[]): any[] {
		const rootGroup = new Group();

		return this.getSubLevel(data, 0, groupByColumns, rootGroup);
	}

	getSubLevel(data: any[], level: number, groupByColumns: string[], parent: Group): any[] {
		// Recursive function, stop when there are no more levels.
		if (level >= groupByColumns.length) {
			return data;
		}

		const groups = this.uniqueBy(
			data.map((row) => {
				const result = new Group();
				result.level = level + 1;
				result.parent = parent;
				for (let i = 0; i <= level; i++) {
					result[groupByColumns[i]] = row.dataModel[groupByColumns[i]];
				}
				return result;
			}),
			JSON.stringify,
		);

		const currentColumn = groupByColumns[level];

		let subGroups = [];
		groups.forEach((group) => {
			let rowsInGroup = data.filter((row) => group[currentColumn] === row.dataModel[currentColumn]);
			group.count = rowsInGroup.length;
			if (this.groupTitleColumn) {
				group.title = rowsInGroup[0].dataModel[this.groupTitleColumn];
			}

			const groupReduced = this.reducedGroups.some((g) => JSON.stringify(g) === JSON.stringify(group));

			if (groupReduced) {
				rowsInGroup = [];
				group.reduced = true;
			}
			const subGroup = this.getSubLevel(rowsInGroup, level + 1, groupByColumns, group);
			subGroup.unshift(group);

			subGroups = subGroups.concat(subGroup);
		});

		return subGroups;
	}

	groupHeaderClick(row) {
		if (!row.reduced) {
			this.reducedGroups.push(row);
		} else {
			const sortByColumn = this.getGroupFields()[0];
			this.reducedGroups = this.reducedGroups.filter((obj) => obj[sortByColumn] !== row[sortByColumn]);
			row.reduced = false;
		}

		this.applyGrouping();
	}

	getGroupFields(): string[] {
		if (this.groupBy) {
			return [this.groupBy];
		} else {
			return [];
		}
	}
	uniqueBy(a, key) {
		const seen = {};
		return a.filter(function (item) {
			const k = key(item);
			return seen.hasOwnProperty(k) ? false : (seen[k] = true);
		});
	}

	private sortFn(a, b, acs: boolean = true): number {
		if (acs) {
			return a === b ? 0 : a < b ? -1 : 1;
		} else {
			return a === b ? 0 : a > b ? -1 : 1;
		}
	}

	//#endregion
}
